import React, { useCallback, useRef, useEffect, useMemo } from 'react';
import { format, differenceInMinutes } from 'date-fns';
import { DayShift } from '@/types';
import { Employee } from '@/types';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Clock,
  User,
  MapPin,
  Users,
  Calendar,
  Timer,
  AlertTriangle,
  Lock
} from 'lucide-react';
import { ShiftStatus, SHIFT_STATUS_CONFIG } from '@/lib/scheduling/shift-status-types';
import { getShiftStatusBorderColor } from '@/lib/scheduling/shift-status-styles';
import { getZIndexClass } from '@/lib/scheduling/z-index-layers';
import { useDimming } from '@/hooks/ui/useDimming';
import { useUnifiedShift } from '@/components/shifts/context/UnifiedShiftProvider';
import { convertUtcDateToBusinessTimezone } from '@/lib/core/timezone-utils';

interface ShiftTooltipProps {
  children: React.ReactNode;
  shift: DayShift;
  employees?: Employee[];
  status?: ShiftStatus;
  actualArrivalTime?: string;
  actualDepartureTime?: string;
  delayDuration?: number;
  side?: 'top' | 'bottom' | 'left' | 'right';
  showDetailedInfo?: boolean;
  disabled?: boolean;
  scrollContainerRef?: React.RefObject<HTMLElement>;
  businessTimezone?: string; // Optional timezone prop for context-free usage
  isProtected?: boolean; // Whether shift is locked (past shift, needs "Editing Past" mode)
}

export const ShiftTooltip: React.FC<ShiftTooltipProps> = ({
  children,
  shift,
  employees = [],
  status,
  actualArrivalTime,
  actualDepartureTime,
  delayDuration = 500,
  side = 'top',
  showDetailedInfo = true,
  disabled = false,
  scrollContainerRef,
  businessTimezone: propBusinessTimezone,
  isProtected = false
}) => {
  // Get business timezone from prop or context with fallback
  let contextBusinessTimezone: string | undefined;
  try {
    const context = useUnifiedShift();
    contextBusinessTimezone = context.businessTimezone;
  } catch {
    // Context not available, will use prop or default
    contextBusinessTimezone = undefined;
  }

  const businessTimezone = propBusinessTimezone || contextBusinessTimezone || 'UTC';
  const {
    shouldShowTooltip,
    getTooltipSide,
    setActiveTooltipShiftId,
    shiftWithOpenSelect,
    activeTooltipShiftId
  } = useDimming();

  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Track if THIS tooltip instance set the active tooltip (for cleanup)
  const didSetTooltipRef = useRef<boolean>(false);
  
  // Find employee data
  const assignedEmployee = employees.find(emp => emp.id === shift.employeeId);
  
  // Calculate shift duration
  const shiftDurationMinutes = differenceInMinutes(shift.endTime, shift.startTime);
  const durationHours = Math.floor(shiftDurationMinutes / 60);
  const durationMins = shiftDurationMinutes % 60;
  
  // Format times safely using explicit UTC to business timezone conversion
  const formatTime = (date: Date) => {
    try {
      // Convert UTC Date object to business timezone
      const businessTime = convertUtcDateToBusinessTimezone(date, businessTimezone);
      return format(businessTime, 'HH:mm');
    } catch {
      // Fallback to simple format without timezone conversion
      try {
        return format(date, 'HH:mm');
      } catch {
        return '--:--';
      }
    }
  };
  
  const formatDateTime = (dateString: string) => {
    try {
      const utcDate = new Date(dateString);
      // Convert UTC timestamp to business timezone
      const businessTime = convertUtcDateToBusinessTimezone(utcDate, businessTimezone);
      return format(businessTime, 'MMM d, HH:mm');
    } catch {
      return 'Invalid date';
    }
  };
  
  // Handle tooltip show with coordination
  const handleTooltipShow = useCallback(() => {
    if (disabled || shiftWithOpenSelect) return;

    // Clear any existing timeout
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }

    // Set timeout for showing tooltip
    tooltipTimeoutRef.current = setTimeout(() => {
      // Double-check conditions before showing
      if (!shiftWithOpenSelect && !disabled) {
        setActiveTooltipShiftId(shift.id, side);
        didSetTooltipRef.current = true;
      }
      tooltipTimeoutRef.current = null;
    }, delayDuration);
  }, [disabled, shift.id, side, delayDuration, setActiveTooltipShiftId, shiftWithOpenSelect]);

  // Handle tooltip hide
  const handleTooltipHide = useCallback(() => {
    // Clear timeout if still pending
    if (tooltipTimeoutRef.current) {
      clearTimeout(tooltipTimeoutRef.current);
      tooltipTimeoutRef.current = null;
    }

    // Always request to clear tooltip - the context will handle it
    // Only clear if this tooltip instance set it OR if it's currently showing for this shift
    if (didSetTooltipRef.current || activeTooltipShiftId === shift.id) {
      setActiveTooltipShiftId(null);
      didSetTooltipRef.current = false;
    }
  }, [setActiveTooltipShiftId, activeTooltipShiftId, shift.id]);

  // Clear tooltip when select opens - handled by DimmingContext now
  // This effect is kept as a backup safety measure
  useEffect(() => {
    if (shiftWithOpenSelect && didSetTooltipRef.current) {
      didSetTooltipRef.current = false;
    }
  }, [shiftWithOpenSelect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear any pending timeout
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
        tooltipTimeoutRef.current = null;
      }

      // Clear tooltip if this component set it
      if (didSetTooltipRef.current) {
        setActiveTooltipShiftId(null);
        didSetTooltipRef.current = false;
      }
    };
  }, [setActiveTooltipShiftId]);
  
  // Determine if tooltip should be shown
  const shouldShow = shouldShowTooltip(shift.id);
  const tooltipSide = getTooltipSide(shift.id, side);
  
  // Get status config if status is provided
  const statusConfig = useMemo(() => {
    return status ? SHIFT_STATUS_CONFIG[status] : null;
  }, [status]);
  const StatusIcon = statusConfig?.icon;
  
  // Get status-specific border color
  const borderClass = useMemo(() => {
    return status ? getShiftStatusBorderColor(status) : 'border';
  }, [status]);
  
  // Memoize duration calculation
  const durationText = useMemo(() => {
    if (durationHours === 0 && durationMins === 0) return 'No duration';
    let text = '';
    if (durationHours > 0) text += `${durationHours}h `;
    if (durationMins > 0) text += `${durationMins}m`;
    return text.trim();
  }, [durationHours, durationMins]);
  
  if (disabled) {
    return <>{children}</>;
  }
  
  // Note: TooltipProvider is hoisted to ComposedShiftProvider
  return (
    <Tooltip
      open={shouldShow}
        delayDuration={0} // We handle delay manually
        onOpenChange={(open) => {
          if (!open) {
            handleTooltipHide();
          }
        }}
      >
        <TooltipTrigger
          asChild
          onMouseEnter={handleTooltipShow}
          onMouseLeave={handleTooltipHide}
        >
          {children}
        </TooltipTrigger>
        <TooltipContent
          side={tooltipSide}
          className={`w-80 min-h-[120px] p-3 bg-background ${borderClass} shadow-lg ${getZIndexClass('TOOLTIP')}`}
          sideOffset={8}
          avoidCollisions={true}
          collisionBoundary={scrollContainerRef?.current || undefined}
          collisionPadding={16}
          sticky="partial"
        >
          <div className="space-y-2">
            {/* Header with title */}
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {shift.title || 'Untitled Shift'}
                  </span>
                  {/* Protected/Locked shift indicator */}
                  {isProtected && (
                    <Badge className="text-xxs px-1.5 py-0 flex items-center gap-1">
                      <Lock className="h-2.5 w-2.5" />
                      Locked
                    </Badge>
                  )}
                </div>
                {status && statusConfig && (
                  <div className="flex items-center gap-2 mt-1">
                    {StatusIcon && <StatusIcon className="h-3 w-3" />}
                    <span className="text-xs text-muted-foreground">
                      {statusConfig.label}
                    </span>
                    {/* Historical shift indicator */}
                    {shift.isHistorical && (
                      <Badge variant="outline" className="text-xxs px-1 py-0">
                        Historical
                      </Badge>
                    )}
                  </div>
                )}
                {/* Protected shift explanation */}
                {isProtected && (
                  <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Enable "Editing Past" mode to modify
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Shift Details - Horizontal layout to be wider than taller */}
            <div className="grid grid-cols-2 gap-4 min-h-[50px]">
              {/* Left column */}
              <div className="space-y-1">
                {/* Time */}
                <div className="flex items-center gap-2 text-xs text-foreground">
                  <Clock className="h-3 w-3 text-muted-foreground" />
                  <span>
                    {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                  </span>
                </div>

                {/* Duration */}
                <div className="flex items-center gap-2 text-xs text-foreground">
                  <Timer className="h-3 w-3 text-muted-foreground" />
                  <span>{durationText}</span>
                </div>
              </div>

              {/* Right column */}
              <div className="space-y-1">
                {/* Employee */}
                <div className="flex items-center gap-2 text-xs text-foreground">
                  <User className="h-3 w-3 text-muted-foreground" />
                  <span>
                    {assignedEmployee ? assignedEmployee.name : 'Unassigned'}
                  </span>
                </div>

                {/* Shift requests if any */}
                {shift.requestCount > 0 && !shift.employeeId && (
                  <div className="flex items-center gap-2 text-xs text-foreground">
                    <Users className="h-3 w-3 text-muted-foreground" />
                    <span>
                      {shift.requestCount} employee{shift.requestCount > 1 ? 's' : ''} requested
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Time tracking details - always present but may be empty */}
            {showDetailedInfo && (
              <>
                <Separator />
                <div className="min-h-[25px]">
                  <div className="text-xs font-medium text-foreground mb-1">Time Log</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex justify-between text-xs text-foreground">
                      <span>Arrived:</span>
                      <span>{actualArrivalTime ? formatDateTime(actualArrivalTime) : '--'}</span>
                    </div>
                    <div className="flex justify-between text-xs text-foreground">
                      <span>Left:</span>
                      <span>{actualDepartureTime ? formatDateTime(actualDepartureTime) : '--'}</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Status information section - always present with consistent height */}
            <>
              <Separator />
              <div className="min-h-[25px]">
                {status === ShiftStatus.MISSED ? (
                  <>
                    <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                      <AlertTriangle className="h-3 w-3" />
                      <span>Employee did not show up for this shift</span>
                    </div>
                    <div className="text-xxs text-muted-foreground mt-1">
                      No arrival time was recorded
                    </div>
                  </>
                ) : status && statusConfig?.requiresAttention ? (
                  <div className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400">
                    <AlertTriangle className="h-3 w-3" />
                    <span>{statusConfig.description}</span>
                  </div>
                ) : status && statusConfig ? (
                  <div className="text-xs text-muted-foreground">
                    {statusConfig.description}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground opacity-0">
                    No additional status information
                  </div>
                )}
              </div>
            </>

          </div>
        </TooltipContent>
    </Tooltip>
  );
};

// Simplified version for basic use cases
export const SimpleShiftTooltip: React.FC<Omit<ShiftTooltipProps, 'showDetailedInfo'>> = (props) => (
  <ShiftTooltip {...props} showDetailedInfo={false} />
);

// Version with longer delay for calendar views
export const DelayedShiftTooltip: React.FC<Omit<ShiftTooltipProps, 'delayDuration'>> = (props) => (
  <ShiftTooltip {...props} delayDuration={1000} />
);

export default ShiftTooltip;